import { useCallback, useEffect, useState } from 'react'
import { enqueueSnackbar } from "notistack"
import { ExperienceRating, ExperienceRatingWeights } from '@app/components/common/ExperienceRatingScale/types'
import { FeedbackPrompt, SaveFeedbackPrompt } from '@app/types'
import { saveFeedbackResponse } from '@app/services/feedback'
import { useDebouncedProperty } from './debouncedProperty'

export const useFeedbackSubmit = (
  feedbackPrompt: FeedbackPrompt
) => {
  const [selectedRate, setSelectedRate] = useState<ExperienceRating>(null)
  const [openResponse, setOpenResponse] = useState<string>("")
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [debouncedSelectedRate] = useDebouncedProperty(selectedRate, 500)

  const onChangeFeedbackRating = useCallback(
    (value: ExperienceRating) => {
      if (submitted) return

      setSelectedRate(value)
    },
    [submitted]
  )

  const resetFeedbackRating = () => {
    setSelectedRate(null)
    setOpenResponse("")
  }

  const isCombinedFeedback =
    feedbackPrompt?.promptType === "OPEN_RESPONSE_RATING"
  const hasRatingToSubmit = selectedRate !== null

  const submitFeedback = useCallback(async () => {
    if (!selectedRate) {
      console.warn("[Student Feedback Submission]: No rating selected")
      return false
    }

    if (!feedbackPrompt?.responseId) {
      console.warn("[Student Feedback Submission]: responseId is undefined")
      resetFeedbackRating()
      return false
    }

    if (submitted) {
      return true
    }

    const promptResponse = String(ExperienceRatingWeights[selectedRate]) || ""
    const textResponse =
      feedbackPrompt.promptType === "OPEN_RESPONSE_RATING" &&
      openResponse.trim() !== ""
        ? openResponse
        : null

    const feedbackResponseData: SaveFeedbackPrompt = {
      promptResponse: promptResponse,
      textResponse: textResponse,
      responseId: feedbackPrompt?.responseId
    }

    try {
      await saveFeedbackResponse(feedbackResponseData)

      /* 
        TODO: Inline styling is used temporarily due to the absence of a UI standard for qti-pwa.
          * Used in order to make the snackbar message show up AND look as close as possible to the one in READY
        TODO: Refactor this once qti-pwa is integrated into the monorepo with standardized UI support.
      */
      enqueueSnackbar("Submission successful. Thank you for your feedback.", {
        variant: "success",
        style: {
          width: '480px',
          minWidth: '480px',
          maxWidth: '90%',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          borderRadius: '4px',
          padding: '0 16px',
          backgroundColor: '#4c7f2a',
          color: '#ffffff',
          fontSize: '14px'
        }
      })

      setSubmitted(true)
      return true
    } catch (error) {
      console.warn("[Student Feedback Submission]: api call error - ", error)

      resetFeedbackRating()
      return false
    }
  }, [
    feedbackPrompt?.responseId,
    feedbackPrompt?.promptType,
    selectedRate,
    openResponse,
    submitted
  ])

  useEffect(() => {
    if (isCombinedFeedback || !debouncedSelectedRate || submitted) return

    submitFeedback()
  }, [debouncedSelectedRate, submitFeedback, isCombinedFeedback, submitted])

  return {
    selectedRate,
    onChangeFeedbackRating,
    openResponse,
    setOpenResponse,
    submitted,
    submitFeedback,
    isCombinedFeedback,
    hasRatingToSubmit
  }
}
